import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Loader2 } from 'lucide-react';
import { PROJECT_TYPE_ICONS } from '@/utils/flow-data';

const CollectionNavigation = ({ collectionData, isLoading, currentProjectId }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is our md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getProjectIcon = (type) => {
    const iconData = PROJECT_TYPE_ICONS.find(
      item => item.flow.toLowerCase() === type.toLowerCase()
    );
    return iconData?.icon;
  };

  const renderProjectButton = (project, index) => {
    const ProjectIcon = getProjectIcon(project.type);
    
    return (
      <Button
        key={project.id}
        variant="ghost"
        className={`w-full h-12 mb-1 flex items-center justify-start gap-3 px-2 ${
          project.id === currentProjectId ? 'bg-blue-50 text-blue-600' : ''
        }`}
        onClick={() => navigate(`/project/${project.id}`)}
      >
        <div className={`relative w-10 h-10 rounded-full ${
          project.id === currentProjectId ? 'bg-blue-100' : 'bg-gray-100'
        } flex items-center justify-center flex-shrink-0`}>
          {ProjectIcon && (
            <ProjectIcon className="w-6 h-6" />
          )}
          <span className="absolute -bottom-1 -right-1 w-4 h-4 flex items-center justify-center bg-white rounded-full text-xs border border-gray-200">
            {index + 1}
          </span>
        </div>
        
        {isExpanded && (
          <div className="flex flex-col items-start overflow-hidden">
            <span className="text-sm truncate w-full" title={project.name}>
              {project.name}
            </span>
            <span className="text-xs text-gray-500 capitalize">
              {project.type}
            </span>
          </div>
        )}
      </Button>
    );
  };

  if (!collectionData) return null;

  return (
    <>
      {/* Mobile hamburger button */}
      {isMobile && (
        <Button
          variant="ghost"
          className="fixed left-0 top-16 w-12 h-12 md:hidden flex items-center justify-center z-50 bg-white/30"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex flex-col gap-1">
            <div className="w-4 h-0.5 bg-gray-600"></div>
            <div className="w-4 h-0.5 bg-gray-600"></div>
            <div className="w-4 h-0.5 bg-gray-600"></div>
          </div>
        </Button>
      )}

      {/* Sidebar */}
      <div 
        className={`fixed left-0 top-16 bottom-16 transition-all duration-300 z-40
          ${isExpanded ? 'w-64 bg-white shadow-lg' : 'w-0 md:w-14'}
          ${isExpanded ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${!isExpanded ? 'bg-white shadow-lg md:bg-transparent md:shadow-none' : ''}`}
        onMouseEnter={() => !isMobile && setIsExpanded(true)}
        onMouseLeave={() => !isMobile && setIsExpanded(false)}
      >
        {/* Desktop hamburger button - removed since we're using hover */}
        {!isMobile && (
          <div className="w-full h-12 hidden md:flex items-center justify-center">
            <div className="flex flex-col gap-1">
              <div className="w-4 h-0.5 bg-gray-600"></div>
              <div className="w-4 h-0.5 bg-gray-600"></div>
              <div className="w-4 h-0.5 bg-gray-600"></div>
            </div>
          </div>
        )}

        {/* Collection name header section */}
        <div className="px-2 border-b border-gray-300 pb-4">
          {isLoading ? (
            <div className="flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="space-y-2">
              {isExpanded ? (
                <h2 className="text-sm font-semibold truncate" title={collectionData.name}>
                  {collectionData.name}
                </h2>
              ) : !isMobile ? (
                <div className="w-10 h-10 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-sm font-semibold text-blue-600">
                    {collectionData.name.charAt(0)}
                  </span>
                </div>
              ) : null}
              
              {isExpanded && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => navigate(`/collection/${collectionData.id}/summary`)}
                >
                  View Summary
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Project buttons */}
        <div className={`py-4 ${isExpanded ? 'block' : 'hidden md:block'}`}>
          {collectionData.projects.map((project, index) => 
            renderProjectButton(project, index)
          )}
        </div>
      </div>
    </>
  );
};

export default CollectionNavigation;